import { memo, useCallback, useMemo } from 'react';

import { DataTable } from '@/components/ui/table/data-table';
import type { SortDirection } from '@/shared/types/common';
import { Insight } from '@/shared/types/insights';

import { columns } from './columns';
import { Toolbar } from './Toolbar';

interface InsightTableProps {
  data: Insight[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  currentPageIndex: number;
  setCurrentPageIndex: (index: number) => void;
  rowCount: number;
  typeDirection: SortDirection;
  nameDirection: SortDirection;
  updatedDirection: SortDirection;
  toggleTypeDirection: () => void;
  toggleNameDirection: () => void;
  toggleUpdatedDirection: () => void;
  isLoading?: boolean;
  isFetchingNextPage: boolean;
  onRowClick: (element: Insight) => void;
  onEdit: (insightId: string) => void;
  onDuplicate: (insightId: string) => void;
  onDelete: (insightId: string) => void;
  onCreateInsight: () => void;
  insightsEnabled: boolean;
  onToggleInsights: (checked: boolean) => void;
  canWrite: boolean;
  activeRowId?: string;
  readonly?: boolean;
  emptyStateMessage?: string;
  className?: string;
}

const InsightTableComponent = ({
  data,
  fetchNextPage,
  hasNextPage,
  currentPageIndex,
  setCurrentPageIndex,
  rowCount,
  typeDirection,
  nameDirection,
  updatedDirection,
  toggleTypeDirection,
  toggleNameDirection,
  toggleUpdatedDirection,
  isLoading,
  isFetchingNextPage,
  onRowClick,
  onEdit,
  onDuplicate,
  onDelete,
  onCreateInsight,
  insightsEnabled,
  onToggleInsights,
  canWrite,
  activeRowId,
  readonly = false,
  emptyStateMessage,
  className,
}: InsightTableProps) => {
  const tableColumns = useMemo(
    () =>
      columns({
        typeDirection,
        nameDirection,
        updatedDirection,
        toggleTypeDirection,
        toggleNameDirection,
        toggleUpdatedDirection,
        onEdit,
        onDuplicate,
        onDelete,
        readonly,
      }),
    [
      typeDirection,
      nameDirection,
      updatedDirection,
      toggleTypeDirection,
      toggleNameDirection,
      toggleUpdatedDirection,
      onEdit,
      onDuplicate,
      onDelete,
      readonly,
    ]
  );
  const RenderToolbar = useCallback(() => {
    return (
      <Toolbar
        onCreateInsight={onCreateInsight}
        insightsEnabled={insightsEnabled}
        onToggleInsights={onToggleInsights}
        canWrite={canWrite}
        readonly={readonly}
      />
    );
  }, [onCreateInsight, insightsEnabled, onToggleInsights, canWrite, readonly]);

  return (
    <DataTable.Root<Insight, unknown>
      fill
      className={className}
      columns={tableColumns}
      data={data}
      pagination
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      currentPageIndex={currentPageIndex}
      setCurrentPageIndex={setCurrentPageIndex}
      rowCount={rowCount}
      resourceName="insight"
      enableRowHover
      isFetchingNextPage={isFetchingNextPage}
      onRowClick={onRowClick}
      emptyStateMessage={emptyStateMessage}
      isLoading={isLoading}
      getRowId={(row) => row.insight_id}
      activeRowId={activeRowId}
    >
      <DataTable.Toolbar>{RenderToolbar}</DataTable.Toolbar>
      <DataTable.Content />
      <DataTable.Pagination />
    </DataTable.Root>
  );
};

export const InsightTable = memo(InsightTableComponent);
